import React, {Component} from 'react'
import {Icon} from 'semantic-ui-react'
import {saveClaudeMessage, getClaudeJobStatus} from './claude-save-message'
import {downloadDeck, downloadDeckExcel} from './claude-deck'

// Claude-backed AI assistant: a floating popup chat. Launches from a bottom-right
// button; the panel is draggable by its header and sized for comfortable reading.
// Separate button + endpoint from the Grok ChatButton. Keeps conversation in local
// state and sends the running history so follow-ups ("export that") work.

// Limited testing: only these accounts see the assistant. Empty the list to open it up.
const TEST_EMAILS = ['newerp@ihlgroup.com', 'sami_admin@ihlgroup.com', 'admin@example.com']

const COLORS = {
  black: '#1b1b1b',
  red: '#db2828',
  panelBg: '#ffffff',
  userBubble: '#db2828',
  botBubble: '#f1f3f5'
}

const PANEL_W = 460
const PANEL_H = 600
const POLL_MS = 20000

const DECK_BUTTON = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
  background: COLORS.red, color: '#fff', padding: '9px 16px',
  borderRadius: '8px', fontWeight: 'bold', fontSize: '14px'
}

// Render assistant text: **bold** -> bold, http(s) links -> clickable links.
const renderText = (text) => {
  const str = String(text)
  const regex = /(\*\*[^*]+\*\*)|(https?:\/\/[^\s]+)/g
  const nodes = []
  let lastIndex = 0, key = 0, m
  while ((m = regex.exec(str)) !== null) {
    if (m.index > lastIndex) nodes.push(<span key={key++}>{str.slice(lastIndex, m.index)}</span>)
    if (m[1]) {
      nodes.push(<strong key={key++}>{m[1].slice(2, -2)}</strong>)
    } else {
      nodes.push(<a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer" style={{color: COLORS.red, fontWeight: 'bold'}}>{m[2]}</a>)
    }
    lastIndex = regex.lastIndex
  }
  if (lastIndex < str.length) nodes.push(<span key={key++}>{str.slice(lastIndex)}</span>)
  return nodes
}

class ClaudeChatButton extends Component {
  constructor(props) {
    super(props)
    this.state = {open: false, message: '', messages: [], loading: false, pos: null}
    this.scrollRef = React.createRef()
    this.panelRef = React.createRef()
    this.dragOffset = {x: 0, y: 0}
  }

  componentDidMount() {
    this.pollTimer = setInterval(this.pollJobs, POLL_MS)
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer)
    window.removeEventListener('mousemove', this.onDrag)
    window.removeEventListener('mouseup', this.endDrag)
  }

  toggle = () => this.setState({open: !this.state.open}, this.scrollToBottom)

  // Queued reports and presentations finish minutes later; fill in the button then.
  pollJobs = () => {
    this.state.messages.filter(m => m.pending).forEach(({pending}) => {
      getClaudeJobStatus(pending.id, (err, res) => {
        if (err || res.data.status === 'processing') return
        this.finishJob(pending, res.data)
      })
    })
  }

  finishJob = (pending, {status, download, deck, error}) => {
    const name = pending.kind === 'presentation' ? 'presentation' : 'report'
    const note = status === 'ready' ? `Your ${name} is ready.` : error
    const messages = this.state.messages.map(m => {
      if (!m.pending || m.pending.id !== pending.id) return m
      return {...m, pending: null, download, deck, text: `${m.text}\n\n${note}`}
    })
    this.setState({messages}, this.scrollToBottom)
  }

  scrollToBottom = () => {
    const el = this.scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }

  // --- drag to move (via the header) ---
  startDrag = (e) => {
    const panel = this.panelRef.current
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    this.dragOffset = {x: e.clientX - rect.left, y: e.clientY - rect.top}
    window.addEventListener('mousemove', this.onDrag)
    window.addEventListener('mouseup', this.endDrag)
    e.preventDefault()
  }

  onDrag = (e) => {
    const w = this.panelRef.current ? this.panelRef.current.offsetWidth : PANEL_W
    const h = this.panelRef.current ? this.panelRef.current.offsetHeight : PANEL_H
    let left = Math.max(0, Math.min(e.clientX - this.dragOffset.x, window.innerWidth - w))
    let top  = Math.max(0, Math.min(e.clientY - this.dragOffset.y, window.innerHeight - h))
    this.setState({pos: {left, top}})
  }

  endDrag = () => {
    window.removeEventListener('mousemove', this.onDrag)
    window.removeEventListener('mouseup', this.endDrag)
  }

  sendMessage = () => {
    let {message, messages, loading} = this.state
    if (loading || !message.trim()) return

    const history = messages.map(m => ({role: m.role, text: m.text}))
    const withUser = messages.concat([{role: 'user', text: message}])
    this.setState({message: '', messages: withUser, loading: true}, this.scrollToBottom)

    saveClaudeMessage(message, history, (err, res) => {
      const reply = err ? 'Sorry, something went wrong.' : res.data.text
      const download = err ? null : res.data.download   // {url, filename, count} when a report was generated
      const pending = err ? null : res.data.pending     // {id, kind} when a report or presentation was queued
      const deck = err ? null : res.data.deck           // slide data when a presentation was edited
      this.setState(
        {loading: false, messages: withUser.concat([{role: 'assistant', text: reply, download, pending, deck}])},
        this.scrollToBottom
      )
    })
  }

  onChange = (e) => this.setState({message: e.target.value})

  handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      this.sendMessage()
    }
  }

  render() {
    const {open, message, messages, loading, pos} = this.state

    // Limited testing gate: hide entirely unless the logged-in email is allowed.
    // current auth stores this in localStorage; fall back to sessionStorage for older builds
    const email = (localStorage.getItem('currentUserEmail') || sessionStorage.getItem('currentUserEmail') || '').toLowerCase()
    if (TEST_EMAILS.length > 0 && !TEST_EMAILS.includes(email)) return null

    const launcher = (
      <div onClick={this.toggle}
        style={{
          position: 'fixed', bottom: '70px', right: '20px', zIndex: 10000,
          width: '56px', height: '56px', borderRadius: '50%',
          background: COLORS.black, color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `2px solid ${COLORS.red}`,
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)'
        }}>
        <Icon name={open ? 'close' : 'comment'} size="large" style={{margin: 0}} />
      </div>
    )

    if (!open) return launcher

    // Anchored bottom-right by default; once dragged, use the free left/top position.
    const placement = pos
      ? {left: `${pos.left}px`, top: `${pos.top}px`}
      : {bottom: '92px', right: '20px'}

    return (
      <div>
        {launcher}
        <div ref={this.panelRef} style={{
          position: 'fixed', zIndex: 10000, ...placement,
          width: `${PANEL_W}px`, maxWidth: 'calc(100vw - 40px)',
          height: `${PANEL_H}px`, maxHeight: 'calc(100vh - 120px)',
          background: COLORS.panelBg, borderRadius: '12px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', border: `1px solid ${COLORS.black}`
        }}>
          {/* Header (drag handle) */}
          <div onMouseDown={this.startDrag} title="Drag to move"
            style={{
              background: COLORS.black, color: '#fff', padding: '14px 18px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: `3px solid ${COLORS.red}`, cursor: 'move', userSelect: 'none'
            }}>
            <span style={{fontWeight: 'bold', fontSize: '17px'}}>
              <Icon name="magic" style={{color: COLORS.red}} /> AI Assistant
            </span>
            <Icon name="close" onMouseDown={(e) => e.stopPropagation()} onClick={this.toggle}
              style={{cursor: 'pointer', margin: 0, fontSize: '18px'}} />
          </div>

          {/* Messages */}
          <div ref={this.scrollRef} style={{flex: 1, overflowY: 'auto', padding: '16px'}}>
            {messages.length === 0 &&
              <div style={{color: '#888', fontSize: '15px', lineHeight: 1.5, textAlign: 'center', marginTop: '24px'}}>
                Ask about inventory — e.g. "how many red styles are in stock" or "export everything oversold".
              </div>
            }
            {messages.map((x, i) =>
              <div key={i} style={{display: 'flex', justifyContent: x.role === 'user' ? 'flex-end' : 'flex-start', margin: '10px 0'}}>
                <div style={{
                  maxWidth: '82%', padding: '11px 15px', borderRadius: '16px', fontSize: '15.5px', lineHeight: 1.45,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: x.role === 'user' ? COLORS.userBubble : COLORS.botBubble,
                  color: x.role === 'user' ? '#fff' : '#111'
                }}>
                  {renderText(x.text)}
                  {x.download &&
                    <div style={{marginTop: '10px'}}>
                      <a href={x.download.url} download={x.download.filename} target="_blank" rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          background: COLORS.red, color: '#fff', padding: '9px 16px',
                          borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold', fontSize: '14px'
                        }}>
                        <Icon name="file excel outline" style={{margin: 0}} /> Download report
                        {x.download.count != null ? ` (${x.download.count})` : ''}
                      </a>
                    </div>
                  }
                  {x.deck &&
                    <div style={{marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                      <span onClick={() => downloadDeck(x.deck)} style={DECK_BUTTON}>
                        <Icon name="file powerpoint outline" style={{margin: 0}} /> Download PowerPoint
                      </span>
                      <span onClick={() => downloadDeckExcel(x.deck)} style={DECK_BUTTON}>
                        <Icon name="file excel outline" style={{margin: 0}} /> Tables as Excel
                      </span>
                    </div>
                  }
                  {x.pending &&
                    <div style={{marginTop: '10px', color: '#666', fontSize: '14px'}}>
                      <Icon loading name="spinner" /> Generating…
                    </div>
                  }
                </div>
              </div>
            )}
            {loading &&
              <div style={{color: '#888', fontSize: '15px', margin: '10px 0'}}>…thinking</div>
            }
          </div>

          {/* Input */}
          <div style={{borderTop: '1px solid #eee', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px'}}>
            <input
              value={message} onChange={this.onChange} onKeyDown={this.handleKeyDown}
              placeholder="Ask anything…" autoComplete="off"
              style={{flex: 1, border: '1px solid #ddd', borderRadius: '8px', outline: 'none', padding: '11px 12px', fontSize: '15px'}}
            />
            <Icon name="send" size="large" onClick={this.sendMessage}
              style={{cursor: loading ? 'not-allowed' : 'pointer', color: COLORS.red, margin: 0}} />
          </div>
        </div>
      </div>
    )
  }
}

export default ClaudeChatButton
